import { fetchAuthSession } from "aws-amplify/auth";
import type { AllowanceResponse } from "@spectacle/chat-view-spec";
import { isAllowanceResponse } from "./allowance";

/**
 * Requests to the chat service that are not a turn.
 *
 * Kept apart from ./transport on purpose: that module pulls in the Ai Sdk, and
 * `GET /chat/allowance` is read from services/apis/api.ts, which every page
 * loads. Importing the transport there would put the Sdk in the main bundle.
 */

export const CHAT_API_URL: string = import.meta.env.VITE_CHAT_API_URL ?? "";

export function chatApiUrl(baseUrl: string, path: string): string {
    return `${baseUrl.replace(/\/+$/, "")}${path}`;
}

/**
 * Who is asking, and about which workspace: the two headers every request to
 * the chat service carries, turns and `GET /chat/allowance` alike. Both go
 * through the same authorizer, so both present the same credentials.
 */
export async function chatAuthHeaders(
    workspaceId: string | undefined,
): Promise<Record<string, string>> {
    const { tokens } = await fetchAuthSession();
    if (!tokens?.accessToken) {
        throw new Error("No access token");
    }

    if (!workspaceId) {
        throw new Error("No workspace selected");
    }

    return {
        Authorization: `Bearer ${tokens.accessToken}`,
        "Spectacle-Workspace-Id": workspaceId,
    };
}

/**
 * `GET /chat/allowance`: the state the service would enforce if a turn were
 * sent now.
 *
 * Throws on anything but a well-formed 200. The caller treats every failure
 * alike -- no meter, the composer stays open, and a turn's own refusal
 * decides -- so which way it failed only matters to the console.
 */
export async function fetchChatAllowance(
    workspaceId: string,
    baseUrl: string = CHAT_API_URL,
): Promise<AllowanceResponse> {
    const response = await fetch(chatApiUrl(baseUrl, "/chat/allowance"), {
        headers: {
            ...(await chatAuthHeaders(workspaceId)),
            Accept: "application/json",
        },
    });

    if (!response.ok) {
        throw new Error(`Chat allowance answered ${response.status}`);
    }

    const body: unknown = await response.json();
    if (!isAllowanceResponse(body)) {
        throw new Error("Chat allowance answered an unexpected shape");
    }

    return body;
}
